export interface Options<T, K extends keyof T> {
  /** The key to compare in the objects */
  key: K;
  /** Sort descending? defaults to false (i.e. ascending) */
  desc?: boolean;
  /** The type of comparison you wish to make on the values, will default to 'string' */
  type?: 'string' |'number' | 'date';
  /** The locale to use when doing string comparisons, will default to ´'en'´ */
  locale?: string;
}

// eslint-disable-next-line max-len
const isString = (input: unknown): input is string => (typeof input === 'string' || input instanceof String)
// eslint-disable-next-line max-len
const isNumber = (input: unknown): input is number => !Number.isNaN(input as number) && (typeof input === 'number')
const isDate = (input: unknown): input is Date => typeof (input as Date).getMonth === 'function'
// eslint-disable-next-line max-len
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isMissingValue<T extends {[key: string]: any}>(obj: T, key: unknown): boolean {
  return (
    !(Object.prototype.hasOwnProperty.call(obj, key as string)) // must have the key
      || (obj[key as string] === undefined)
      || (obj[key as string] === null)
  )
}

/**
 * Returns a function that compares two key/val objects by the value of the specified
 * key and returns an indication on what value comes first. The functions can be used
 * to sort arrays of objects as the inner `compareFunction` argument in js' `Array.sort()`:
 *
 * ```ts
   const myArr = [
    { aKey: 'b' },
    { aKey: 'c' },
    { aKey: 'a' },
   ]
   myArr.sort(compareObjectsByKeyValue({
    key: 'aKey', // specify the key to compare by
   }))

   console.log(myArr) // [ { aKey: 'a' }, { aKey: 'b' }, { aKey: 'c' } ]
 *```
 *
 * The function handles string-to-string, number-to-number and date-to-date
 * comparisons, depending on the options specified. In itself the returned
 * function takes two objects, compare their values at the specified key and
 * returns:
 *
 *   + `-1` if the first objects' value comes before the second objects' value,
 *   + `0` if two values are equal,
 *   + `1` if the second objects' value comes before the first objects' value
 *
 * ### Notes:
 *
 * If an object does not have the specified key, or the key has a value
 * of `null` or `undefined`, it will be treated as a 'missing value' and
 * rank BEFORE all other non-missing values (e.g. 'empty' values will come
 * first when used with `Array.sort()`).
 *
 * Otherwise, if the value of the key is of another type than you specified as
 * the `type` argument in the functions options, it will be ranked LAST, after
 * all other values, with these exceptions:
 *
 * - in a `type = 'string'` comparison, numbers will be converted to strings, e.g.
 * `121` will be treated as the string `'121'`.
 *
 * - in a `type = 'number'` comparison, strings will be straightforwardly converted
 * to numbers if possible, e.g. `'121'` will be treated as the number `121`, but
 * `'121a'` or `''` (empty string) will be treated as a non-number and therefore
 * rank last after all other numbers.
 *
 * @param param0
 */
export function compareObjectsByKeyValue<T extends object, K extends keyof T>({
  key,
  desc = false,
  type = 'string',
  locale = 'en',
}: Options<T, K>) {
  const innerFn = (a: T, b: T): number => {
    let comparison = 0 // 0 = no difference (default)
    const getInOrder = (x: number) => (desc ? (x * -1) : x)

    // "missing values" should come first in sort order:
    if (isMissingValue(a, key)) {
      if (isMissingValue(b, key)) return 0 // both does not have the value, then they have equal rank
      return (getInOrder(-1)) // only a is missing a value, then a before b
    }

    if (isMissingValue(b, key)) {
      return (getInOrder(1)) // only b is missing a value, then b before a
    }

    // eslint-disable-next-line prefer-const
    let aVal = a[key]
    // eslint-disable-next-line prefer-const
    let bVal = b[key]

    if (type === 'string') {
      // try to cast to string if they are numbers
      if (isNumber(aVal)) (aVal as unknown as string) = (aVal as number).toString()
      if (isNumber(bVal)) (bVal as unknown as string) = (bVal as number).toString()

      // a is string
      if (isString(aVal)) {
        // both strings
        if (isString(bVal)) {
          // eslint-disable-next-line eqeqeq
          if (aVal == bVal) return 0
          comparison = (aVal as string).localeCompare(bVal as string, locale)
          return getInOrder(comparison)
        }
        // only a is string, then b should be last
        return getInOrder(-1)
      }
      // neither a or b are strings, then they have the same rank
      if (!isString(bVal)) return 0

      // b is string (a not), then a should be last:
      return getInOrder(1)
    }

    if (type === 'number') {
      // if strings cast to number
      if (isString(bVal) && bVal.trim() !== '') (bVal as unknown as number) = +(bVal as string) // plus operator will 'cast' to number if possible
      if (isString(aVal) && aVal.trim() !== '') (aVal as unknown as number) = +(aVal as string) // plus operator will 'cast' to number if possible

      if (isNumber(aVal)) {
        // both are numbers
        if (isNumber(bVal)) {
          // eslint-disable-next-line eqeqeq
          if (aVal == bVal) return 0
          comparison = ((aVal as number) < (bVal as number)) ? -1 : 1
          return getInOrder(comparison)
        }
        // only a is number, then b should be last
        return getInOrder(-1)
      }
      // neither a or b are numbers, then they have the same rank:
      if (!isNumber(bVal)) return 0

      // b is number, but not a, then a should be last:
      return getInOrder(1)
    }

    if (type === 'date') {
      if (isDate(aVal)) {
        // both are dates:
        if (isDate(bVal)) {
          // eslint-disable-next-line eqeqeq
          if ((aVal as unknown as Date).getTime() === (bVal as unknown as Date).getTime()) return 0
          comparison = ((aVal as unknown as Date) < (bVal as unknown as Date)) ? -1 : 1
          return getInOrder(comparison)
        }
        // only a is date, then b should be last
        return getInOrder(-1)
      }
      // neither a or b are dates, then they have the same rank
      if (!isDate(bVal)) return 0

      // b is date, but not a, then a should be last:
      return getInOrder(1)
    }
    return getInOrder(comparison)
  }
  return innerFn
}
